/* eslint-disable camelcase */
import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useState,
} from 'react';
import { Button, Alert, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FaFacebook } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';

import cx from 'classnames';
import { useMounted } from 'hooks/useIsMounted';
import { Validation } from 'utils/validationForm';

import { AuthContext } from 'contexts/authContext';

import s from './SignInForm.module.scss';

type SignInFormProps = {
  onLoggedIn?(): void;
};
const SignInForm: FunctionComponent<SignInFormProps> = ({ onLoggedIn }) => {
  const isMounted = useMounted();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [isReset, setIsReset] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);

  const {
    actions,
    state: { loading, ...state },
  } = useContext(AuthContext);

  const login = useCallback(
    async (data: any) => {
      const success = await actions.login(data);
      if (success && onLoggedIn) {
        onLoggedIn();
      }
    },
    [actions, onLoggedIn],
  );

  const resetPassword = useCallback(
    async ({ email }: any) => {
      await actions.resetPassword({ email });
      setResetLinkSent(true);
    },
    [actions],
  );

  const loginOrReset = useCallback(
    async (data: any) => {
      if (isReset) {
        await resetPassword(data);
        return;
      }
      await login(data);
    },
    [isReset, login, resetPassword],
  );

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!isMounted) {
          return;
        }
        handleSubmit(loginOrReset)(e);
      }}
    >
      <Row
        className={cx(
          'justify-content-between mx-0 mb-4',
          s['social-media-buttons'],
        )}
      >
        <Button
          className="d-flex align-items-center btn-squared p-2 btn-shadow btn-mobile-full-rounded"
          type="button"
          variant="outline-light"
          size="sm"
          onClick={async () => {
            const success = await actions.loginFacebook();
            if (success && onLoggedIn) {
              onLoggedIn();
            }
          }}
        >
          <FaFacebook
            size="25px"
            color="#1877F2"
            className="mr-2 flex-shrink-0"
          />
          <span>Sign in with Facebook</span>
        </Button>
        <Button
          className="d-flex align-items-center btn-shadow btn-squared p-2"
          size="sm"
          type="button"
          variant="outline-light"
          onClick={async () => {
            const success = await actions.loginGoogle();
            if (success && onLoggedIn) {
              onLoggedIn();
            }
          }}
        >
          <FcGoogle size="25px" className="mr-2 flex-shrink-0" />
          <span>Sign in with Google</span>
        </Button>
      </Row>
      {(!resetLinkSent && (
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="text"
            {...register('email', {
              required: Validation.required,
              pattern: Validation.email,
            })}
            placeholder="email"
            className="form-control"
          />
          {errors?.email && <small>{errors?.email.message as string}</small>}
        </div>
      )) || (
        <div className="my-5">
          <h3>
            If your email is valid a reset link has been sent to your email
            account. No Account?{' '}
            <a href="/checkout?createNewAccountTabActive=true">
              you can create one here
            </a>
          </h3>
        </div>
      )}

      {!isReset && (
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            {...register('password', {
              required: Validation.required,
            })}
            placeholder="password"
            className="form-control"
            autoComplete="off"
          />
          {errors?.password && (
            <small>{errors?.password.message as string}</small>
          )}
        </div>
      )}
      {!isReset && state.error && (
        <Alert variant="danger" className="my-5">
          {state.error.message}
        </Alert>
      )}

      <div className="form-group mb-0 d-flex flex-md-row flex-column justify-content-between">
        {!resetLinkSent && (
          <Button
            type="submit"
            disabled={!isMounted || loading}
            variant="outline-primary"
          >
            {!isReset ? 'Login' : 'Send reset link'}
          </Button>
        )}

        <Button
          type="button"
          variant="link"
          className="pl-0 pr-0"
          onClick={() => {
            actions.clearError();
            setIsReset(!isReset);
            setResetLinkSent(false);
          }}
        >
          {!isReset ? 'Forgot my password' : 'Back to login'}
        </Button>
      </div>
    </form>
  );
};

export default SignInForm;
